import React, { useEffect, useMemo, useRef, useState } from 'react';

import { skillStoreService, StoreSkill } from '../services/skillStoreService';

import { buildLandingSkillChoices, LandingSkillChoice, withoutSkillBuilder } from './landingChoices';

const SEARCH_DEBOUNCE_MS = 180;
const MAX_VISIBLE_SEARCH_RESULTS = 8;
const MAX_SKILL_SEARCH_LENGTH = 120;

export const useLandingSkillOptions = () => {
  const [skills, setSkills] = useState<StoreSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;

    void skillStoreService.listLandingSkills().then((items) => {
      if (!active) return;
      setSkills(items);
      setUnavailable(false);
    }).catch(() => {
      if (!active) return;
      setSkills([]);
      setUnavailable(true);
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  return { skills, loading, unavailable };
};

const filterSkillChoices = (choices: LandingSkillChoice[], query: string): LandingSkillChoice[] => {
  const cleanQuery = query.trim().toLocaleLowerCase();

  if (!cleanQuery) return choices.slice(0, MAX_VISIBLE_SEARCH_RESULTS);

  return choices.filter((choice) => (
    choice.displayName.toLocaleLowerCase().includes(cleanQuery)
    || choice.skill?.name.toLocaleLowerCase().includes(cleanQuery)
    || choice.description.toLocaleLowerCase().includes(cleanQuery)
  )).slice(0, MAX_VISIBLE_SEARCH_RESULTS);
};

const mergeSkillResults = (initialSkills: StoreSkill[], searchedSkills: StoreSkill[]): StoreSkill[] => {
  const byId = new Map(initialSkills.map((skill) => [skill.id, skill]));

  searchedSkills.forEach((skill) => byId.set(skill.id, skill));

  return Array.from(byId.values());
};

// eslint-disable-next-line max-statements
export const useSkillSearch = (
  initialSkills: StoreSkill[],
  open: boolean,
  query: string,
  includeSkillBuilder: boolean,
): { choices: LandingSkillChoice[]; searching: boolean; unavailable: boolean } => {
  const [response, setResponse] = useState<{ query: string; skills: StoreSkill[] } | null>(null);
  const [pendingQuery, setPendingQuery] = useState('');
  const [failedQuery, setFailedQuery] = useState('');
  const cleanQuery = query.trim();

  useEffect(() => {
    if (!open || !cleanQuery) return undefined;

    let active = true;
    const timer = window.setTimeout(() => {
      setPendingQuery(cleanQuery);
      void skillStoreService.listLandingSkills(cleanQuery).then((items) => {
        if (!active) return;
        setResponse({ query: cleanQuery, skills: items });
        setFailedQuery('');
      }).catch(() => {
        if (active) setFailedQuery(cleanQuery);
      }).finally(() => {
        if (active) setPendingQuery((pending) => pending === cleanQuery ? '' : pending);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [cleanQuery, open]);

  const merged = cleanQuery && response?.query === cleanQuery
    ? mergeSkillResults(initialSkills, response.skills)
    : initialSkills;
  const results = includeSkillBuilder ? merged : withoutSkillBuilder(merged);

  const choices = useMemo(
    () => filterSkillChoices(buildLandingSkillChoices(results), query),
    [query, results],
  );

  return {
    choices,
    searching: Boolean(cleanQuery) && pendingQuery === cleanQuery,
    unavailable: Boolean(cleanQuery) && failedQuery === cleanQuery,
  };
};

interface SkillSearchPanelProps {
  choices: LandingSkillChoice[];
  query: string;
  searching: boolean;
  unavailable: boolean;
  onQueryChange: (query: string) => void;
  onSelect: (choice: LandingSkillChoice) => void;
  onClose: () => void;
}

const useActiveSearchChoice = (choices: LandingSkillChoice[], activeIndex: number) => {
  const safeActiveIndex = Math.min(activeIndex, Math.max(choices.length - 1, 0));
  const activeOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [safeActiveIndex]);

  return { safeActiveIndex, activeChoice: choices[safeActiveIndex], activeOptionRef };
};

const SearchResult: React.FC<{
  choice: LandingSkillChoice;
  active: boolean;
  optionRef?: React.RefObject<HTMLButtonElement | null>;
  onHover: () => void;
  onSelect: () => void;
}> = ({ choice, active, optionRef, onHover, onSelect }) => (
  <button
    type="button"
    role="option"
    id={`landing-skill-option-${choice.id}`}
    ref={optionRef}
    className={`landing-skill-search__result${active ? ' is-active' : ''}`}
    aria-selected={active}
    tabIndex={-1}
    onMouseEnter={onHover}
    onClick={onSelect}
    data-skill-id={choice.id}
  >
    <span className="landing-skill-search__result-icon">
      <i
        className={`fa-solid ${choice.kind === 'builder' ? 'fa-cube' : 'fa-wand-magic-sparkles'}`}
        aria-hidden="true"
      />
    </span>
    <span className="landing-skill-search__result-copy">
      <strong>{choice.displayName}</strong>
      <small>{choice.description}</small>
    </span>
    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
  </button>
);

// eslint-disable-next-line max-lines-per-function
export const SkillSearchPanel: React.FC<SkillSearchPanelProps> = ({
  choices,
  query,
  searching,
  unavailable,
  onQueryChange,
  onSelect,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => inputRef.current?.focus(), []);

  const { safeActiveIndex, activeChoice, activeOptionRef } = useActiveSearchChoice(choices, activeIndex);

  // eslint-disable-next-line max-statements
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();

      return;
    }
    if (!choices.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % choices.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + choices.length) % choices.length);
    } else if (event.key === 'Enter' && activeChoice) {
      event.preventDefault();
      onSelect(activeChoice);
    }
  };

  return (
    <div className="landing-skill-search" role="dialog" aria-label="Find a reusable skill">
      <div className="landing-skill-search__input-row">
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          value={query}
          maxLength={MAX_SKILL_SEARCH_LENGTH}
          onChange={(event) => {
            setActiveIndex(0);
            onQueryChange(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search skills by name"
          aria-label="Search skills by name"
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls="landing-skill-search-results"
          aria-activedescendant={activeChoice ? `landing-skill-option-${activeChoice.id}` : undefined}
          data-testid="landing-skill-search-input"
        />
        {searching && <i className="fa-solid fa-circle-notch landing-skill-search__spinner" aria-hidden="true" />}
        <button type="button" onClick={onClose} aria-label="Close skill search">
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      </div>
      <div id="landing-skill-search-results" className="landing-skill-search__results" role="listbox">
        {choices.map((choice, index) => (
          <SearchResult
            key={choice.id}
            choice={choice}
            active={index === safeActiveIndex}
            optionRef={index === safeActiveIndex ? activeOptionRef : undefined}
            onHover={() => setActiveIndex(index)}
            onSelect={() => onSelect(choice)}
          />
        ))}
        {!searching && !choices.length && !unavailable && (
          <p className="landing-skill-search__empty">No matching skills</p>
        )}
        {!searching && unavailable && (
          <p className="landing-skill-search__empty">Skill search is temporarily unavailable.</p>
        )}
      </div>
    </div>
  );
};
